import { useId } from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { Card } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/hen-ui/shadcn/accordion';

import { CareerExplorerRecommendationCardHeader } from './CareerExplorerRecommendationCardHeader';
import { CareerExplorerRecommendationDetails } from './CareerExplorerRecommendationDetails';
import { CareerExplorerRecommendationEnrollment } from './CareerExplorerRecommendationEnrollment';
import type { CareerExplorerRecommendation } from './types';

interface CareerExplorerRecommendationCardProps {
  program: CareerExplorerRecommendation;
}

export const CareerExplorerRecommendationCard = ({
  program,
}: CareerExplorerRecommendationCardProps) => {
  const titleId = useId();
  const partnerId = useId();

  return (
    <Card
      asChild
      trackable={false}
      variant='background-white'
      border='stroke'
      padding='none'
      rounded='md'
    >
      <AccordionItem
        value={program.id}
        className='w-full items-stretch overflow-hidden [&>*]:w-full'
      >
        <AccordionTrigger
          aria-labelledby={titleId}
          aria-describedby={program.partnerLogoAlt ? partnerId : undefined}
          className='p-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-700 focus-visible:ring-offset-2 data-[state=open]:p-sm [&_[data-slot=accordion-trigger-icon]]:-mt-min [&_[data-slot=accordion-trigger-icon]]:self-start [&_[data-slot=accordion-trigger-icon]]:text-neutral-900 [&_[data-slot=accordion-trigger-icon]]:[stroke-width:1.5]'
        >
          <CareerExplorerRecommendationCardHeader
            title={program.title}
            titleId={titleId}
            partnerId={partnerId}
            partnerLogoUrl={program.partnerLogoUrl}
            partnerLogoAlt={program.partnerLogoAlt}
          />
        </AccordionTrigger>

        <AccordionContent className='p-0'>
          <Flex direction='column' gap='l1' className='px-sm pb-sm'>
            <Separator className='bg-neutral-200' />

            <Text variant='b5' className='text-neutral-900'>
              {program.description}
            </Text>

            <CareerExplorerRecommendationDetails details={program.details} />

            <Flex
              direction='row'
              gap='l1'
              align='center'
              justify='between'
              wrap='wrap'
              width='full'
              data-slot='recommendation-actions'
            >
              {program.enrollment && (
                <CareerExplorerRecommendationEnrollment
                  {...program.enrollment}
                />
              )}
              <Button
                href={program.ctaHref}
                variant='primaryFill'
                size='sm'
                className='ml-auto bg-primary-700 px-md hover:!bg-primary-700 active:!bg-primary-700 aria-pressed:!bg-primary-700'
              >
                {program.ctaLabel}
              </Button>
            </Flex>
          </Flex>
        </AccordionContent>
      </AccordionItem>
    </Card>
  );
};
